/**
 * The Better Auth browser client, pointed at the auth service's admin
 * deployment.
 *
 * ── `baseURL` IS set here, unlike the login UI ───────────────────────
 *
 * `apps/login` in the auth repo deliberately omits it, because that app is
 * served from the same origin as the API and hardcoding an origin there would
 * silently make it cross-origin everywhere except production.
 *
 * This app is the opposite case: it is a separate origin by design, so the API
 * origin is something it must be told. Baked in at build time — see
 * vite.config.ts for why an environment is a build here.
 *
 * ── `credentials: 'include'` is not optional ─────────────────────────
 *
 * Different origin means the session cookie is not sent unless the request asks
 * for it. Without this every call is unauthenticated and the portal looks like
 * it has been signed out — which is indistinguishable from actually being
 * signed out, and sends the investigation at the session rather than at fetch
 * options.
 *
 * It works at all because `admin.ridgewill.com` and `auth.ridgewill.com` share
 * a registrable domain: same-site, so `SameSite=Lax` still sends the cookie.
 * The auth service has to allow this origin in `TRUSTED_ORIGINS` and set
 * `COOKIE_DOMAIN=.ridgewill.com`, or the cookie is never scoped widely enough
 * to arrive.
 */
import { createAuthClient } from 'better-auth/react';
import { adminClient, inferAdditionalFields } from 'better-auth/client/plugins';

/**
 * Trailing slash stripped so `${AUTH_URL}/api/auth/...` never produces a double
 * slash — which some proxies normalise and some 404, making it an environment
 * -dependent bug rather than a visible one.
 */
export const AUTH_URL = (import.meta.env.VITE_AUTH_URL ?? '').replace(/\/$/, '');

if (!AUTH_URL) {
  // Loud at module load rather than at the first request. A blank base produces
  // requests to this app's own origin, which 404 against the static host and
  // read as "the API is down".
  console.error('[org-admin] VITE_AUTH_URL is not set — every API call will go to the wrong origin');
}

/**
 * Where a person goes to SIGN IN, which is not where this app reads its data.
 *
 * ── Two origins, because the deployment is split ─────────────────────
 *
 * `AUTH_URL` is the ADMIN deployment. It serves `/admin`, `/api-key`,
 * `/open-api` and the session read, and it refuses everything else — including
 * `/sign-in/email`, deliberately, because a second place to present credentials
 * is a second place to get the cookie, the rate limiting and the origin checks
 * wrong.
 *
 * So the sign-in link cannot point there. It points at the hot-path deployment,
 * which serves the hosted login UI and the endpoint behind it. The session
 * cookie is scoped to `.ridgewill.com`, so the session that origin creates is
 * the one this app reads from the other.
 *
 * ── Falling back to AUTH_URL is the single-deployment case ───────────
 *
 * Unset means both halves are one service — local development, and any
 * environment where the split has not been stood up. Defaulting to `AUTH_URL`
 * keeps those working unchanged instead of producing a sign-in link to nowhere.
 */
export const LOGIN_URL =
  (import.meta.env.VITE_LOGIN_URL ?? '').replace(/\/$/, '') || AUTH_URL;

export const authClient = createAuthClient({
  baseURL: AUTH_URL,
  fetchOptions: { credentials: 'include' },
  plugins: [
    adminClient(),
    /**
     * Mirrors the server's `additionalFields`. `input: false` marks them
     * server-owned, exactly as in the auth repo's client — without it the
     * compiler treats them as required inputs on calls that never send them.
     */
    inferAdditionalFields({
      user: {
        role: { type: 'string', input: false },
        pendingAgreements: { type: 'string[]', input: false },
      },
    }),
  ],
});

export const { useSession, signOut } = authClient;

/**
 * Where a person manages their OWN account — a different product, on purpose.
 *
 * ── This portal deliberately does not do it ──────────────────────────
 *
 * #63 settled that password and MFA belong to the account portal (#10), not
 * here. The old portal carried them because it was also the account UI, and
 * undoing that conflation is the point rather than an omission.
 *
 * A settings modal was built here and then removed rather than shipped: even
 * routed to the hot path so the admin deployment grew no account surface, it
 * still made this app the place you change a password — which is the thing #63
 * decided it should stop being. A link is the honest shape.
 *
 * ── The portal exists now, and the deploy names it ───────────────────
 *
 * `VITE_ACCOUNT_URL` is set in deploy.yml and checked into the built bundle
 * alongside the other two origins, because this link is one menu item deep:
 * unset, the fallback below quietly takes over and nothing else on the portal
 * looks wrong.
 *
 * The fallback STAYS. It is not dead code — it is what a preview build or a
 * misconfigured deploy renders, and `/forgot-password` is a real page rather
 * than a menu item that opens nothing. What it must not become is the normal
 * case: an admin who came to change an avatar or turn on two-factor being
 * offered a password reset is a worse answer than either.
 */
export const ACCOUNT_URL = (import.meta.env.VITE_ACCOUNT_URL ?? '').replace(/\/$/, '');

/** Where "Account settings" goes: the account portal, or the hosted login's
 *  reset flow if this bundle was built without one. */
export const accountSettingsUrl = (): string =>
  ACCOUNT_URL || `${LOGIN_URL}/forgot-password`;
